import { useEffect, useRef, useState } from 'react'
import { Button, Col, Container, Form } from 'react-bootstrap'
import {
  ContentPanel,
  DenyRequestModal,
  DropdownAccordion,
  ProjectUsersTable,
  SearchInput,
  SearchSplash,
  Toolbar
} from '../../components'
import { useActions, useApplicationName } from '../../store'
import { Helmet } from 'react-helmet-async'
import {
  useAdminAffiliations,
  useProjectUserMutation,
  useProjectUsersExportMutation,
  useProjectUsersSearch
} from '../../hooks'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faCloudDownloadAlt } from '@fortawesome/free-solid-svg-icons'
import { PAGE_STATE, PERMISSION, UserType } from '../../helpers'

const NEW_USERS = { id: 1, name: 'New User Requests' }
const EXISTING_USERS = { id: 2, name: 'Manage Project Users' }
const options = [NEW_USERS, EXISTING_USERS]

const ProjectUsersAdminPage = () => {
  const applicationName = useApplicationName()
  const [selected, setSelected] = useState(options[0])
  const [userType, setUserType] = useState(UserType.New)
  const [affiliation, setAffiliation] = useState()
  const [search, setSearch] = useState('')
  const [noResultText, setNoResultText] = useState('')
  const [page, setPage] = useState(1)
  const [sort, setSort] = useState([])
  const [refetchFlag, setRefetchFlag] = useState(false)
  const [showDenyRequestModal, setShowDenyRequestModal] = useState(false)
  const { progressStart, progressEnd, setPageState } = useActions()
  const userArgsRef = useRef()

  const { affiliations } = useAdminAffiliations({ all: true })
  const { users, pageCount, refetch } = useProjectUsersSearch({
    affiliationId: affiliation?.id,
    search,
    userType,
    page,
    sort
  })
  const { mutate: updateUser } = useProjectUserMutation()
  const { mutate: exportUsers } = useProjectUsersExportMutation()

  useEffect(() => {
    switch (selected) {
      case NEW_USERS:
        setUserType(UserType.New)
        progressStart()
        refetch().finally(() => progressEnd())
        break
      case EXISTING_USERS:
        setUserType(UserType.Existing)
        progressStart()
        refetch().finally(() => progressEnd())
        break
      default:
    }
  }, [refetch, selected, progressStart, progressEnd])

  useEffect(() => {
    setPage(1)
    setSort([])
    setSearch('')
    setNoResultText(
      userType === UserType.New
        ? 'There is no new user request'
        : 'Sorry, no results found'
    )
    setRefetchFlag(Boolean(userType))
  }, [userType])

  useEffect(() => {
    if (refetchFlag) {
      progressStart()
      refetch().finally(() => progressEnd())
      setRefetchFlag(false)
    }
  }, [refetch, refetchFlag, progressStart, progressEnd])

  const usersLength = users?.length
  useEffect(() => {
    setPageState(usersLength ? PAGE_STATE.EDIT : PAGE_STATE.DEFAULT)
  }, [setPageState, usersLength])

  const handleExport = () =>
    exportUsers({ affiliationId: affiliation?.id, search, sort, userType })

  const handleAffiliationChange = (event) => {
    setAffiliation(
      affiliations.find((a) => a.id === Number(event.target.value))
    )
    setPage(1)
    setSort([])
    setRefetchFlag(true)
  }

  const handleSearch = () => {
    setPage(1)
    setSort([])
    setRefetchFlag(true)
  }

  const handlePageChange = (data) => {
    const newPage = data.selected + 1
    if (newPage !== page) {
      setPage(data.selected + 1)
      setRefetchFlag(true)
    }
  }

  const handleSort = (data) => {
    if (data !== sort) {
      setPage(1)
      setSort(data)
      setRefetchFlag(true)
    }
  }

  const handleUserUpdate = () =>
    updateUser(userArgsRef.current, {
      onSuccess: () => {
        const user = users.find((user) => user.id === userArgsRef.current.id)
        user[userArgsRef.current.property] = userArgsRef.current.value
      }
    })

  const handleUpdate = (id, property, value) => {
    userArgsRef.current = {
      id,
      property,
      value
    }
    if (property === 'permission' && value === PERMISSION.DENIED) {
      setShowDenyRequestModal(true)
    } else {
      handleUserUpdate()
    }
  }

  const handleDenyRequestModalCancel = () => setShowDenyRequestModal(false)

  const handleDenyRequestModalConfirm = (args) => {
    setShowDenyRequestModal(false)
    userArgsRef.current = { ...userArgsRef.current, extra: args }
    handleUserUpdate()
  }

  return (
    <Container className='resizing-container'>
      <Helmet>
        <title>Project User Management - {applicationName}</title>
      </Helmet>
      <Toolbar title='Project User Management'>
        <DropdownAccordion
          id='adminSelect'
          title='Select'
          options={options}
          selectedOption={selected}
          onOptionSelect={setSelected}
          variant='link'
        />
      </Toolbar>
      <ContentPanel>
        <h2>{selected.name}</h2>
        <Form.Row className='pb-3'>
          <Col md={4} lg={3}>
            <Form.Control
              as='select'
              aria-label='group'
              value={affiliation ? affiliation.id : '-1'}
              onChange={handleAffiliationChange}
            >
              <option value='-1'>Show All</option>
              {affiliations?.map((affiliation) => (
                <option key={affiliation.id} value={affiliation.id}>
                  {affiliation.name}
                </option>
              ))}
            </Form.Control>
          </Col>
          <Col md={4} lg={3}>
            <SearchInput
              placeholder='Search by name or email'
              search={search}
              onSearchChange={setSearch}
              onSearch={handleSearch}
            />
          </Col>
          <Col className='text-right'>
            <Button
              className='pl-0 pr-1 text-dark'
              onClick={handleExport}
              title='Export Data'
              variant='link'
            >
              <FontAwesomeIcon icon={faCloudDownloadAlt} /> Export Data
            </Button>
          </Col>
        </Form.Row>
        {users ? (
          users.length ? (
            <ProjectUsersTable
              users={users}
              userType={userType}
              page={page - 1}
              pageCount={pageCount}
              onPageChange={handlePageChange}
              sort={sort}
              onSort={handleSort}
              onUpdate={handleUpdate}
            />
          ) : (
            <SearchSplash text={noResultText} />
          )
        ) : (
          <SearchSplash text='Find a user among all registered users by searching for their name, username, or email' />
        )}
        <DenyRequestModal
          show={showDenyRequestModal}
          onCancel={handleDenyRequestModalCancel}
          onConfirm={handleDenyRequestModalConfirm}
        />
      </ContentPanel>
    </Container>
  )
}

export { ProjectUsersAdminPage }
